import { useEffect, useReducer, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Layout, Search, Activity, HelpCircle, MessageCircle, LayoutGrid, List, Calendar as CalendarIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Board } from "@/types/board";
import BoardCard from "@/components/BoardCard";
import { StatsSection } from "@/components/StatsSection";
import LogoutButton from "@/components/LogoutButton";
import AnimatedBackground from "@/components/AnimatedBackground";
import { api } from "@/lib/api";

import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { auth } from "@/lib/firebase";
import { Cheatsheet } from "@/components/Cheatsheet";
import { CalendarView } from "@/components/CalendarView";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Define state types
interface BoardsState {
  boards: Board[];
  isLoading: boolean;
  loadingError: string | null;
  searchQuery: string;
  newBoardTitle: string;
  isDialogOpen: boolean;
  showSubscriptionModal: boolean;
  showDeleteConfirm: boolean;
  boardToDelete: string | null;
  viewMode: 'grid' | 'cheatsheet' | 'calendar';
}

// Define action types
type BoardsAction =
  | { type: 'SET_BOARDS'; payload: Board[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_NEW_BOARD_TITLE'; payload: string }
  | { type: 'SET_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_SUBSCRIPTION_MODAL'; payload: boolean }
  | { type: 'SET_DELETE_CONFIRM'; payload: { show: boolean; boardId: string | null } }
  | { type: 'SET_VIEW_MODE'; payload: 'grid' | 'cheatsheet' | 'calendar' };

// Initial state
const initialState: BoardsState = {
  boards: [],
  isLoading: true,
  loadingError: null,
  searchQuery: '',
  newBoardTitle: 'New DoDue',
  isDialogOpen: false,
  showSubscriptionModal: false,
  showDeleteConfirm: false,
  boardToDelete: null,
  viewMode: 'grid',
};

// Reducer function
function boardsReducer(state: BoardsState, action: BoardsAction): BoardsState {
  switch (action.type) {
    case 'SET_BOARDS':
      return { ...state, boards: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, loadingError: action.payload };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'SET_NEW_BOARD_TITLE':
      return { ...state, newBoardTitle: action.payload };
    case 'SET_DIALOG_OPEN':
      return { ...state, isDialogOpen: action.payload };
    case 'SET_SUBSCRIPTION_MODAL':
      return { ...state, showSubscriptionModal: action.payload };
    case 'SET_DELETE_CONFIRM':
      return {
        ...state,
        showDeleteConfirm: action.payload.show,
        boardToDelete: action.payload.boardId
      };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    default:
      return state;
  }
}

const BoardsWithErrorBoundary = () => (
  <ErrorBoundary>
    <Boards />
  </ErrorBoundary>
);

const Boards: React.FC = memo(() => {
  // 1. State hooks
  const [state, dispatch] = useReducer(boardsReducer, initialState);

  // 2. Context hooks
  const isPremium = true; // app is free — all features unlocked
  const { toast } = useToast();
  const navigate = useNavigate();



  const filteredBoards = useMemo(() =>
    state.boards.filter((board) =>
      board.title.toLowerCase().includes(state.searchQuery.toLowerCase())
    ),
    [state.boards, state.searchQuery]
  );

  // 4. Callbacks
  const CACHE_KEY = 'doduelife_boards_cache';

  const fetchBoards = useCallback(async () => {
    try {
      if (!auth.currentUser) { navigate('/login'); return; }

      const boards = await api.get<any[]>('/boards?with_columns=1');
      const boardsWithData = boards.map((board: any) => ({
        ...board,
        columns: (board.columns || []).map((col: any) => ({
          ...col,
          order: col.col_order ?? col.order ?? 0,
          cards: (col.cards || []).map((card: any) => ({
            ...card,
            order: card.card_order ?? card.order ?? 0,
          })),
        })),
      }));

      localStorage.setItem(CACHE_KEY, JSON.stringify(boardsWithData));
      dispatch({ type: 'SET_BOARDS', payload: boardsWithData });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      console.error('Error in fetchBoards:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch boards' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [navigate]);

  // 5. Effects
  useEffect(() => {
    console.log('Initializing boards...');
    let mounted = true;
    let timeoutIds: NodeJS.Timeout[] = [];
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000;

    const initializeBoards = async () => {
      if (!mounted) return;

      try {
        const isAuth = !!auth.currentUser;
        if (!isAuth) { navigate('/login'); return; }

        // Show cached boards instantly while fresh data loads in background
        const cached = localStorage.getItem('doduelife_boards_cache');
        if (cached) {
          try {
            dispatch({ type: 'SET_BOARDS', payload: JSON.parse(cached) });
            dispatch({ type: 'SET_LOADING', payload: false });
          } catch { /* ignore bad cache */ }
        }

        await fetchBoards();
      } catch (error) {
        console.error('Error initializing boards:', error);
        if (mounted && retryCount < maxRetries) {
          retryCount++;
          const timeoutId = setTimeout(initializeBoards, retryDelay * retryCount);
          timeoutIds.push(timeoutId);
        } else {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize boards' });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    };

    // Set up board event subscription with debounce
    const handleBoardUpdate = () => {
      if (!mounted) return;

      const timeoutId = setTimeout(() => {
        if (mounted) {
          console.log('Board update event received, fetching boards...');
          fetchBoards().catch(error => {
            console.error('Error fetching boards after update:', error);
          });
        }
      }, 500); // Debounce board updates
      timeoutIds.push(timeoutId);
    };

    console.log('Setting up board event subscription');
    boardEvents.on(BOARD_UPDATED, handleBoardUpdate);

    // Initialize with a small delay to prevent race conditions
    const initTimeoutId = setTimeout(initializeBoards, 100);
    timeoutIds.push(initTimeoutId);

    return () => {
      console.log('Cleanup: clearing timeouts and mounted flag');
      mounted = false;
      timeoutIds.forEach(id => clearTimeout(id));
      boardEvents.off(BOARD_UPDATED, handleBoardUpdate);
    };
  }, [fetchBoards, navigate]);

  // 6. Event handlers
  const handleCreateBoard = useCallback(async () => {
    if (!state.newBoardTitle.trim()) {
      toast({
        title: "Error",
        description: "Board title cannot be empty",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!auth.currentUser) { navigate('/login'); return; }
      await api.post('/boards', { title: state.newBoardTitle });

      dispatch({ type: 'SET_DIALOG_OPEN', payload: false });
      dispatch({ type: 'SET_NEW_BOARD_TITLE', payload: 'New DoDue' });
      await fetchBoards();

      toast({
        title: "Success",
        description: "Board created successfully",
      });
    } catch (error) {
      console.error('Error creating board:', error);
      toast({
        title: "Error",
        description: "Failed to create board",
        variant: "destructive",
      });
    }
  }, [state.newBoardTitle, navigate, toast, fetchBoards]);

  const handleDeleteBoard = async (boardId: string) => {
    dispatch({ type: 'SET_DELETE_CONFIRM', payload: { show: true, boardId } });
  };

  const confirmDelete = async () => {
    if (!state.boardToDelete) return;

    try {
      await api.delete(`/boards/${state.boardToDelete}`);

      toast({
        title: "Success",
        description: "Board deleted successfully",
      });

      dispatch({
        type: 'SET_BOARDS',
        payload: state.boards.filter(b => b.id !== state.boardToDelete)
      });
    } catch (error) {
      console.error('Error deleting board:', error);
      toast({
        title: "Error",
        description: "Failed to delete board",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: 'SET_DELETE_CONFIRM', payload: { show: false, boardId: null } });
    }
  };

  // 8. Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <AnimatedBackground />

      {/* Header Section */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/[0.02] border-b border-white/[0.05]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Layout className="h-6 w-6 text-indigo-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                My DoDue Space
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {isPremium && (
                <Button
                  onClick={() => navigate('/analytics')}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <Activity className="h-5 w-5 mr-1" />
                  Analytics
                </Button>
              )}
              <Button
                onClick={() => navigate('/tutorial')}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                <HelpCircle className="h-5 w-5 mr-1" />
                Tutorial
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                <MessageCircle className="h-5 w-5 mr-1" />
                Contact
              </Button>
              <LogoutButton />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {state.loadingError ? (
          <div className="text-center py-8">
            <div className="text-red-400 mb-4">{state.loadingError}</div>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : state.isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-pulse text-slate-400">Loading your DoDue space...</div>
          </div>
        ) : (
          <>
            <StatsSection boards={state.boards} />

            {/* Search and Actions Bar */}
            <div className="flex items-center justify-between mb-8 bg-white/[0.02] rounded-xl p-2 border border-white/[0.05]">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search DoDue..."
                  value={state.searchQuery}
                  onChange={(e) => dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value })}
                  className="pl-10 bg-white/[0.02] border-white/[0.05] text-white placeholder:text-slate-400 focus-visible:ring-indigo-400"
                />
              </div>

              <div className="flex items-center gap-2 ml-4">
                <div className="bg-white/[0.05] p-1 rounded-lg flex items-center border border-white/[0.05]">
                  <Button
                    variant={state.viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'grid' })}
                    className={state.viewMode === 'grid' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={state.viewMode === 'cheatsheet' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'cheatsheet' })}
                    className={state.viewMode === 'cheatsheet' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={state.viewMode === 'calendar' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'calendar' })}
                    className={state.viewMode === 'calendar' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </div>
                <Dialog
                  open={state.isDialogOpen}
                  onOpenChange={(open) => dispatch({ type: 'SET_DIALOG_OPEN', payload: open })}
                >
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => dispatch({ type: 'SET_DIALOG_OPEN', payload: true })}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white ml-4"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New DoDue Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border border-white/[0.05]">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create New DoDue Project</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="title" className="text-slate-200">Name your DoDue</Label>
                        <Input
                          id="title"
                          value={state.newBoardTitle}
                          onChange={(e) => dispatch({ type: 'SET_NEW_BOARD_TITLE', payload: e.target.value })}
                          placeholder="Enter board title..."
                          className="bg-white/[0.02] border-white/[0.05] text-white placeholder:text-slate-400 focus-visible:ring-indigo-400"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => dispatch({ type: 'SET_DIALOG_OPEN', payload: false })}
                        className="border-white/[0.05] text-slate-700 hover:bg-white/[0.05]"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateBoard}
                        disabled={!state.newBoardTitle.trim()}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white"
                      >
                        Make DoDue
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Content Area */}
            {state.viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBoards.map((board) => (
                  <BoardCard
                    key={board.id}
                    board={board}
                    onDelete={handleDeleteBoard}
                    onEdit={(board) => console.log('Edit board', board)}
                  />
                ))}
              </div>
            ) : state.viewMode === 'calendar' ? (
              <CalendarView boards={state.boards} onUpdate={fetchBoards} />
            ) : (
              <Cheatsheet boards={state.boards} onUpdate={fetchBoards} />
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={state.showDeleteConfirm}
        onOpenChange={(open) =>
          dispatch({ type: 'SET_DELETE_CONFIRM', payload: { show: open, boardId: open ? state.boardToDelete : null } })
        }
      >
        <DialogContent className="bg-slate-800 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle>Delete Board</DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete this board? This action cannot be undone.
              All cards and columns will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2 justify-end">
            <Button
              variant="outline"
              onClick={() => dispatch({ type: 'SET_DELETE_CONFIRM', payload: { show: false, boardId: null } })}
              className="border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Board
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

Boards.displayName = 'Boards';

export default BoardsWithErrorBoundary;